import React from 'react';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/apiClient';
import '../styles/BlockedAccountScreen.css';

// Get polling interval from env (default: 30 seconds)
const POLLING_INTERVAL = Number(import.meta.env.VITE_INACTIVE_CHECK_INTERVAL || '30') * 1000;

/**
 * BlockedAccountScreen - Displayed when user account is inactive (Web SaaS version)
 *
 * Shows a message explaining that the account is inactive and provides
 * a logout button to allow the user to sign in with a different account.
 *
 * Features:
 * - Polls GET /me every INACTIVE_CHECK_INTERVAL seconds to detect reactivation
 * - Automatically returns to protected shell when account becomes active
 */
export const BlockedAccountScreen: React.FC = () => {
  const { logout, isLoggingIn, authStatus, refreshUser } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [isPolling, setIsPolling] = React.useState(false);
  const [accessDeniedInfo, setAccessDeniedInfo] = React.useState<{ email: string; message: string } | null>(null);
  const [pollingError, setPollingError] = React.useState<string | null>(null);

  const handleChangeAccount = async () => {
    setIsLoggingOut(true);
    try {
      // Local-only logout (no AAD logout URL) to allow switching accounts
      await logout(true);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Polling effect: check /me periodically to detect account reactivation
  React.useEffect(() => {
    console.log('[BlockedAccountScreen] Starting polling every', POLLING_INTERVAL / 1000, 'seconds');

    // Poll function
    const poll = async () => {
      if (isPolling) {
        // Already polling, skip this tick
        return;
      }

      setIsPolling(true);
      setPollingError(null); // Clear previous errors

      try {
        console.log('[BlockedAccountScreen] Polling /me...');
        const response = await api.getMe();

        if (response.success && response.data) {
          const user = response.data;
          console.log('[BlockedAccountScreen] /me response:', {
            email: user.email,
            isactive: user.isactive
          });

          if (user.isactive === true) {
            console.log('[BlockedAccountScreen] Account is now active! Refreshing user profile...');

            // Refresh user in auth context
            if (refreshUser) {
              await refreshUser();
              console.log('[BlockedAccountScreen] User profile refreshed, returning to shell');
            }
          } else {
            console.log('[BlockedAccountScreen] Account still inactive, will retry in', POLLING_INTERVAL / 1000, 'seconds');
            setPollingError(null); // Clear error on successful poll
          }
        } else {
          // Handle error responses
          if (response.code === 'USER_INACTIVE') {
            console.log('[BlockedAccountScreen] Account still inactive (403 USER_INACTIVE)');
            setPollingError(null); // Not really an error, expected state
          } else if (response.code === 'UNAUTHORIZED') {
            console.warn('[BlockedAccountScreen] Token expired (401), need reauth');
            setPollingError('Votre session a expiré. Veuillez vous reconnecter.');
          } else if (response.code === 'NETWORK_ERROR') {
            console.error('[BlockedAccountScreen] Network error:', response.error);
            setPollingError('Erreur de connexion au serveur. Vérification en cours...');
          } else {
            console.error('[BlockedAccountScreen] Failed to poll /me:', response.error);
            setPollingError('Une erreur est survenue lors de la vérification de votre compte.');
          }
        }
      } catch (err) {
        console.error('[BlockedAccountScreen] Polling error:', err);
        setPollingError('Erreur de connexion au serveur. Nouvelle tentative dans quelques secondes...');
      } finally {
        setIsPolling(false);
      }
    };

    // Start polling immediately
    poll();

    // Set up interval
    const intervalId = setInterval(poll, POLLING_INTERVAL);

    // Cleanup on unmount
    return () => {
      console.log('[BlockedAccountScreen] Stopping polling');
      clearInterval(intervalId);
    };
  }, []); // Empty deps: only run on mount/unmount

  return (
    <div className="blocked-account-screen">
      <div className="blocked-account-box">
        {/* Icon */}
        <div className="blocked-account-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>

        {/* Title */}
        <h1 className="blocked-account-title" tabIndex={-1}>
          Compte non autorisé
        </h1>

        {/* Message */}
        {accessDeniedInfo ? (
          <>
            <p className="blocked-account-message">
              Le compte <strong>{accessDeniedInfo.email}</strong> ne permet pas un accès à Corpus.
            </p>
            <p className="blocked-account-message">
              Pour obtenir l'accès, contactez-nous à{' '}
              <a
                href="mailto:contact@corpusjuris.fr"
                className="blocked-account-link"
              >
                contact@corpusjuris.fr
              </a>
            </p>
          </>
        ) : (
          <>
            <p className="blocked-account-message">
              Votre compte n'est pas autorisé à accéder à Corpus.
            </p>
            <p className="blocked-account-message">
              Contactez votre administrateur pour activer votre accès, ou connectez-vous avec un autre compte Microsoft.
            </p>

            {/* Status indicator - only show when polling (not 401) */}
            {pollingError ? (
              <p className="blocked-account-status" style={{ color: '#ef4444', opacity: 1 }}>
                {pollingError}
              </p>
            ) : (
              <p className="blocked-account-status">
                Vérification automatique en cours...
              </p>
            )}
          </>
        )}

        {/* Actions */}
        <div className="blocked-account-actions">
          <button
            className="blocked-account-button blocked-account-button-primary"
            onClick={handleChangeAccount}
            disabled={isLoggingOut || isLoggingIn}
            aria-label="Changer de compte Microsoft"
          >
            {isLoggingOut ? 'Changement...' : 'Changer de compte'}
          </button>
        </div>
      </div>
    </div>
  );
};
